import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Mail, Instagram } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getTranslation } from "@/lib/translations";
import { useLanguage } from "@/hooks/use-language";

export default function CtaSection() {
  const { language } = useLanguage();
  
  const contactOptions = [
    { 
      icon: Phone, 
      title: getTranslation('quickContact', language), 
      description: getTranslation('callDuringWorkHours', language),
      action: "+48 886 695 593",
      href: "tel:+48886695593"
    },
    { 
      icon: Mail, 
      title: getTranslation('emailContact', language), 
      description: getTranslation('sendInquiryDirectly', language),
      action: "kontakt1group.pl@gmail.com",
      href: "mailto:kontakt1group.pl@gmail.com"
    },
    {
      icon: Instagram,
      title: getTranslation('instagramContact', language),
      description: getTranslation('followUsOnInstagram', language),
      action: "@1group.pl",
      href: "https://www.instagram.com/1group.pl?igsh=bjNhaXM4em1rNmtu",
      external: true
    }
  ];

  return (
    <section className="py-16 sm:py-20 md:py-24 lg:py-32 relative overflow-hidden">
      {/* Тло з образом */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center" 
        style={{ 
          backgroundImage: `url(https://storage.googleapis.com/fenado-ai-farm-public/generated/18fe8b3c-69dc-4ebe-ad81-03c767b14634.webp)`,
          filter: 'brightness(0.9)',
        }}
      ></div>
      
      {/* Накладка градієнтова */}
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[hsl(225,85%,25%)] to-[hsl(240,85%,30%)]" style={{ mixBlendMode: 'multiply', opacity: 0.85 }}></div>
      
      {/* Декоративний взір крапок */}
      <div className="absolute inset-0 z-0 opacity-10 dot-pattern"></div>
      
      {/* Декоративні "бульбашки" */}
      <div className="absolute top-20 right-[10%] w-48 sm:w-64 h-48 sm:h-64 rounded-full bg-white opacity-10 blur-3xl float-animation-slow"></div>
      <div className="absolute bottom-20 left-[5%] w-56 sm:w-80 h-56 sm:h-80 rounded-full bg-white opacity-10 blur-3xl float-animation"></div>
      
      <div className="container relative z-10 px-4 sm:px-6 md:px-8">
        <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-12 md:mb-16">
          <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 mb-2 sm:mb-3 text-xs sm:text-sm font-medium rounded-full bg-white/20 text-white backdrop-blur-sm">
            {getTranslation('startCooperation', language)}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 sm:mb-6 text-white px-2 sm:px-0">
            {getTranslation('useOurSupport', language)}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 px-2 sm:px-0">
            {getTranslation('chooseContactMethod', language)}
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 max-w-4xl mx-auto">
          {contactOptions.map((option, index) => (
            <div 
              key={index} 
              className="glass-frosted rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up overflow-hidden border border-white/20" 
              style={{
                animationDelay: `${index * 100}ms`,
                backdropFilter: 'blur(12px)',
                transform: 'translateZ(0)'
              }}
            >
              <div className="p-4 sm:p-5 md:p-6 h-full flex flex-col">
                <div className={`p-2 sm:p-3 rounded-lg w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center mb-3 sm:mb-4 md:mb-5 ${
                  option.icon === Instagram ? 'bg-gradient-to-br from-purple-600 to-pink-600' : 'bg-white/20'
                }`}>
                  <option.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-heading font-semibold mb-2 sm:mb-3 text-white">{option.title}</h3>
                <p className="text-white/80 mb-4 sm:mb-5 text-xs sm:text-sm flex-grow">{option.description}</p>
                
                <Button 
                  asChild 
                  className={`w-full rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl text-xs sm:text-sm py-1.5 sm:py-2 ${
                    option.icon === Instagram ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white' : 'bg-white text-primary hover:bg-white/90'
                  }`}
                >
                  {option.external ? (
                    <a href={option.href} target="_blank" rel="noopener noreferrer">
                      {option.action}
                      <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                    </a>
                  ) : (
                    <Link to={option.href}>
                      {option.action}
                      <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                    </Link>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-10 sm:mt-12 md:mt-16 text-center px-4 sm:px-0">
          <p className="text-white/80 text-xs sm:text-sm">
            {getTranslation('additionalQuestions', language).split('FAQ')[0]}
            <Link to="/faq" className="text-white underline hover:no-underline">FAQ</Link>
            {getTranslation('additionalQuestions', language).split('FAQ')[1]}
          </p>
        </div>
      </div>
    </section>
  );
}